/**
 * AI 工作區 — 通用助理 Drawer
 *
 * 任何 AI 工作區應用模組（議題、生態研究、客戶分析…）都用同一個 drawer：
 * - 多輪對話 + 議題上下文（後端注入，前端不知道內容）
 * - ACTION 區塊解析 + 動作卡片
 * - 動作卡片每個欄位執行前可編輯（修正 AI 偏差）
 * - add_source 卡片 URL 預檢
 * - Markdown 渲染 AI 回覆
 * - 對話超過上限時頂部裁切提示
 *
 * 模組要做的：
 * - 透過 props 傳入自己的 chat / executeAction service 函式
 * - 透過 props 傳入自己的 actionMeta（動作 chip 配色 + 中文名）
 * - 動作執行成功後 onActionExecuted 通知父元件刷新
 */

import React, { useState, useRef, useEffect } from 'react';
import {
  Drawer, Box, Stack, IconButton, Tooltip, Typography, Divider,
  TextField, Button, CircularProgress, Chip, Card, CardContent, Alert,
} from '@mui/material';
import {
  Close as CloseIcon, Send as SendIcon,
  DeleteSweep as ClearIcon, SmartToy as AssistantIcon,
  PlayArrow as ExecIcon, CheckCircle as DoneIcon, Error as FailIcon,
} from '@mui/icons-material';

import { useMessage } from '../../contexts/MessageContext';
import { urlPrecheck } from '../../api/aiWorkspaceClient';
import { MAX_CHAT_API_MESSAGES } from '../../api/aiWorkspaceConstants';
import { parseActions } from './parseActions';
import MarkdownText from '../MarkdownText';
import type {
  ChatMessage, ParsedAssistantMessage, AiWorkspaceServices, ActionMetaMap,
} from './types';

/** 預設的 chip 配色（如果模組沒給 actionMeta，用 type 直接當 label） */
const FALLBACK_ACTION_META: ActionMetaMap = {};

interface Props {
  parentId: number | null;
  parentName?: string;
  open: boolean;
  onClose: () => void;
  width?: number;
  /** 模組注入的 service 函式 */
  services: AiWorkspaceServices;
  /** 模組自訂的動作標籤 + 配色（key 是 action.type） */
  actionMeta?: ActionMetaMap;
  /** 預設聊天提示（首次空對話時顯示為 chip） */
  starterPrompts?: string[];
  /** 動作執行成功後通知父元件刷新 */
  onActionExecuted?: () => void;
}

const AiWorkspaceDrawer: React.FC<Props> = ({
  parentId, parentName, open, onClose, width = 380,
  services, actionMeta = FALLBACK_ACTION_META,
  starterPrompts = [],
  onActionExecuted,
}) => {
  const { showApiError } = useMessage();
  const [messages, setMessages] = useState<Array<
    | { role: 'user'; content: string }
    | { role: 'assistant'; content: string; parsed: ParsedAssistantMessage }
  >>([]);
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const scrollRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, sending]);

  const handleSend = async () => {
    const text = input.trim();
    if (!text || !parentId || sending) return;

    const newUser = { role: 'user' as const, content: text };
    const next: typeof messages = [...messages, newUser];
    setMessages(next);
    setInput('');
    setSending(true);

    const allApiMsgs: ChatMessage[] = next.map(m => ({
      role: m.role, content: m.content,
    }));
    const apiMessages = allApiMsgs.slice(-MAX_CHAT_API_MESSAGES);

    try {
      const resp = await services.chat(parentId, apiMessages);
      const parsed = parseActions(resp.content);
      const newMsgIdx = next.length;
      setMessages([...next, { role: 'assistant', content: resp.content, parsed }]);

      // add_source 動作並行 URL 預檢
      parsed.actions.forEach((act, ai) => {
        if (act.raw.type !== 'add_source' || !act.raw.url) return;
        setMessages(prev => {
          const cp = [...prev];
          const m = cp[newMsgIdx];
          if (m?.role !== 'assistant') return prev;
          m.parsed.actions[ai] = { ...m.parsed.actions[ai], precheck: 'checking' };
          return cp;
        });
        urlPrecheck(act.raw.url, act.raw.source_type).then(res => {
          setMessages(prev => {
            const cp = [...prev];
            const m = cp[newMsgIdx];
            if (m?.role !== 'assistant') return prev;
            m.parsed.actions[ai] = {
              ...m.parsed.actions[ai],
              precheck: res.ok ? 'ok' : 'warn',
              precheckWarning: res.warning || undefined,
            };
            return cp;
          });
        }).catch(() => {
          setMessages(prev => {
            const cp = [...prev];
            const m = cp[newMsgIdx];
            if (m?.role !== 'assistant') return prev;
            m.parsed.actions[ai] = { ...m.parsed.actions[ai], precheck: 'warn',
                                      precheckWarning: '預檢失敗' };
            return cp;
          });
        });
      });
    } catch (err) {
      showApiError(err);
      const errMsg = '（系統錯誤，請稍後重試）';
      setMessages([...next, {
        role: 'assistant', content: errMsg,
        parsed: { text: errMsg, actions: [] },
      }]);
    } finally {
      setSending(false);
    }
  };

  const handleEditField = (msgIdx: number, actIdx: number,
                           key: string, value: string) => {
    setMessages(prev => {
      const cp = [...prev];
      const m = cp[msgIdx];
      if (m.role !== 'assistant') return prev;
      const a = m.parsed.actions[actIdx];
      m.parsed.actions[actIdx] = {
        ...a,
        edited: { ...a.edited, [key]: value },
      };
      return cp;
    });
  };

  const handleExecAction = async (msgIdx: number, actIdx: number) => {
    if (!parentId) return;
    setMessages(prev => {
      const cp = [...prev];
      const m = cp[msgIdx];
      if (m.role !== 'assistant') return prev;
      m.parsed.actions[actIdx] = { ...m.parsed.actions[actIdx], status: 'running' };
      return cp;
    });
    try {
      const action = messages[msgIdx].role === 'assistant'
        ? (messages[msgIdx] as any).parsed.actions[actIdx].edited
        : null;
      if (!action) return;
      const result = await services.executeAction(parentId, action);
      setMessages(prev => {
        const cp = [...prev];
        const m = cp[msgIdx];
        if (m.role !== 'assistant') return prev;
        m.parsed.actions[actIdx] = {
          ...m.parsed.actions[actIdx],
          status: result.ok ? 'done' : 'failed',
          resultMessage: result.message,
        };
        return cp;
      });
      if (result.ok && onActionExecuted) onActionExecuted();
    } catch (err) {
      setMessages(prev => {
        const cp = [...prev];
        const m = cp[msgIdx];
        if (m.role !== 'assistant') return prev;
        m.parsed.actions[actIdx] = {
          ...m.parsed.actions[actIdx],
          status: 'failed',
          resultMessage: (err as any)?.response?.data?.detail || '執行失敗',
        };
        return cp;
      });
    }
  };

  const handleClear = () => {
    if (messages.length === 0) return;
    if (window.confirm('清空目前對話？')) setMessages([]);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) {
      e.preventDefault();
      handleSend();
    }
  };

  return (
    <Drawer
      anchor="right" open={open} onClose={onClose} variant="persistent"
      PaperProps={{ sx: { width, top: 70, height: 'calc(100vh - 70px - 60px)' } }}
    >
      {/* 頭 */}
      <Stack direction="row" alignItems="center" spacing={1}
             sx={{ p: 1.5, borderBottom: '1px solid', borderColor: 'divider' }}>
        <AssistantIcon color="primary" fontSize="small" />
        <Typography variant="subtitle2" sx={{ flex: 1 }}>
          AI 助理
          {parentName && (
            <Typography component="span" variant="caption" color="text.secondary"
                        sx={{ ml: 1 }}>
              · {parentName}
            </Typography>
          )}
        </Typography>
        <Tooltip title="清空對話">
          <span>
            <IconButton size="small" onClick={handleClear} disabled={messages.length === 0}>
              <ClearIcon fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
        <Tooltip title="關閉">
          <IconButton size="small" onClick={onClose}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </Stack>

      {/* 對話區 */}
      <Box ref={scrollRef} sx={{
        flex: 1, overflowY: 'auto', p: 1.5,
        bgcolor: 'grey.50',
        display: 'flex', flexDirection: 'column', gap: 1,
      }}>
        {messages.length > MAX_CHAT_API_MESSAGES && (
          <Alert severity="info" sx={{ py: 0.25, '& .MuiAlert-message': { fontSize: '0.7rem' } }}>
            畫面保留全部 {messages.length} 則歷史；送 AI 時只帶最近 {MAX_CHAT_API_MESSAGES} 則維持脈絡
          </Alert>
        )}
        {messages.length === 0 && !sending && (
          <Box sx={{ textAlign: 'center', color: 'text.secondary', mt: 4 }}>
            <Typography variant="body2" sx={{ mb: 1 }}>
              和 AI 一起整理這個工作區
            </Typography>
            <Typography variant="caption" color="text.secondary"
                        sx={{ display: 'block', mb: 1 }}>
              AI 會建議動作卡片，按「執行」即可一鍵套用
            </Typography>
            <Stack spacing={0.5} alignItems="center">
              {starterPrompts.map((p, i) => (
                <Chip key={i} label={p} size="small" variant="outlined"
                      onClick={() => setInput(p)} />
              ))}
            </Stack>
          </Box>
        )}

        {messages.map((m, i) => {
          if (m.role === 'user') {
            return (
              <Box key={i} sx={{
                alignSelf: 'flex-end', maxWidth: '92%',
                bgcolor: 'primary.light', color: 'primary.contrastText',
                borderRadius: 1.5, px: 1.5, py: 1,
              }}>
                <Typography variant="body2" sx={{
                  whiteSpace: 'pre-wrap', fontSize: '0.85rem', lineHeight: 1.5,
                }}>{m.content}</Typography>
              </Box>
            );
          }

          // assistant
          return (
            <Box key={i} sx={{ alignSelf: 'flex-start', width: '100%' }}>
              {m.parsed.text && (
                <Box sx={{
                  maxWidth: '92%',
                  bgcolor: '#fff', border: '1px solid', borderColor: 'divider',
                  borderRadius: 1.5, px: 1.5, py: 1, mb: 0.5,
                }}>
                  <MarkdownText>{m.parsed.text}</MarkdownText>
                </Box>
              )}

              {m.parsed.actions.map((act, ai) => {
                const meta = actionMeta[act.raw.type] || {
                  label: act.raw.type, color: 'primary' as const,
                };
                const isDone = act.status === 'done';
                const isFailed = act.status === 'failed';
                const isRunning = act.status === 'running';

                return (
                  <Card key={ai} variant="outlined" sx={{
                    mb: 0.5,
                    borderColor: isDone ? 'success.main' : isFailed ? 'error.main' : 'divider',
                    bgcolor: isDone ? 'success.50' : isFailed ? 'error.50' : '#fff',
                  }}>
                    <CardContent sx={{ p: 1.25, '&:last-child': { pb: 1.25 } }}>
                      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 0.5 }}>
                        <Chip label={meta.label} size="small" color={meta.color}
                              sx={{ height: 20, fontSize: '0.7rem' }} />
                        {isDone && <DoneIcon fontSize="small" color="success" />}
                        {isFailed && <FailIcon fontSize="small" color="error" />}

                        {/* 預檢「進行中」放這裡（小 spinner，不打擾）；
                            預檢「通過」不秀任何東西（綠卡片邊框已是足夠提示）；
                            預檢「失敗」由下方 Alert 完整顯示 */}
                        {act.raw.type === 'add_source' && act.precheck === 'checking' && (
                          <Tooltip title="正在檢查 URL 是否存活…">
                            <CircularProgress size={12} />
                          </Tooltip>
                        )}
                      </Stack>

                      {/* URL 預檢失敗：唯一的警示 UI（不再額外做 chip） */}
                      {act.raw.type === 'add_source' && act.precheck === 'warn' && act.precheckWarning && (
                        <Alert severity="warning"
                               sx={{ mt: 0.5, mb: 0.5, py: 0,
                                     '& .MuiAlert-message': { fontSize: '0.72rem' } }}>
                          <strong>URL 預檢失敗：</strong>{act.precheckWarning}
                        </Alert>
                      )}

                      <Stack spacing={0.5}>
                        {Object.entries(act.edited)
                          .filter(([k]) => k !== 'type' && k !== 'reason')
                          .map(([k, v]) => {
                            const wasEdited = act.raw[k] !== v;
                            return (
                              <Stack key={k} direction="row" alignItems="center" spacing={0.5}>
                                <Typography variant="caption" sx={{
                                  fontFamily: 'monospace', fontSize: '0.7rem',
                                  minWidth: 100, color: 'text.secondary',
                                }}>
                                  {k}:
                                </Typography>
                                <TextField
                                  value={v}
                                  size="small" variant="outlined" fullWidth
                                  disabled={isDone || isFailed || isRunning}
                                  onChange={e => handleEditField(i, ai, k, e.target.value)}
                                  inputProps={{
                                    style: { fontFamily: 'monospace', fontSize: '0.7rem',
                                             padding: '4px 6px' },
                                  }}
                                  sx={{
                                    '& .MuiOutlinedInput-notchedOutline': {
                                      borderColor: wasEdited ? 'warning.main' : 'divider',
                                    },
                                  }}
                                />
                                {wasEdited && (
                                  <Tooltip title={`AI 原值：${act.raw[k]}`}>
                                    <Typography variant="caption"
                                                sx={{ color: 'warning.main', fontSize: '0.65rem' }}>
                                      已改
                                    </Typography>
                                  </Tooltip>
                                )}
                              </Stack>
                            );
                          })}
                      </Stack>

                      {act.raw.reason && (
                        <Typography variant="caption" color="text.secondary"
                                    sx={{ display: 'block', mt: 0.5, fontStyle: 'italic' }}>
                          理由：{act.raw.reason}
                        </Typography>
                      )}

                      {!isDone && !isFailed && (
                        <Typography variant="caption" color="text.secondary"
                                    sx={{ display: 'block', mt: 0.5, fontSize: '0.65rem' }}>
                          💡 欄位可編輯；AI 推錯的值（item_id、地區、URL 等）請先改正再按執行
                        </Typography>
                      )}

                      {act.resultMessage && (
                        <Alert severity={isDone ? 'success' : 'error'}
                               sx={{ mt: 0.5, py: 0, '& .MuiAlert-message': { fontSize: '0.7rem' } }}>
                          {act.resultMessage}
                        </Alert>
                      )}

                      {!isDone && !isFailed && (
                        <Stack direction="row" spacing={1} sx={{ mt: 0.75 }}>
                          <Button size="small" variant="contained" color={meta.color === 'default' ? 'primary' : meta.color}
                                  startIcon={isRunning ? <CircularProgress size={12} color="inherit" /> : <ExecIcon />}
                                  disabled={isRunning}
                                  onClick={() => handleExecAction(i, ai)}
                                  sx={{ minHeight: 24, py: 0.25, fontSize: '0.7rem' }}>
                            {isRunning ? '執行中…' : '執行'}
                          </Button>
                          <Button size="small" variant="text" color="inherit"
                                  onClick={() => {
                                    setMessages(prev => {
                                      const cp = [...prev];
                                      const mm = cp[i];
                                      if (mm.role !== 'assistant') return prev;
                                      mm.parsed.actions[ai] = {
                                        ...mm.parsed.actions[ai],
                                        status: 'failed',
                                        resultMessage: '已略過',
                                      };
                                      return cp;
                                    });
                                  }}
                                  sx={{ minHeight: 24, py: 0.25, fontSize: '0.7rem' }}>
                            略過
                          </Button>
                        </Stack>
                      )}
                    </CardContent>
                  </Card>
                );
              })}
            </Box>
          );
        })}

        {sending && (
          <Box sx={{ alignSelf: 'flex-start', display: 'flex', alignItems: 'center', gap: 1, p: 1 }}>
            <CircularProgress size={14} />
            <Typography variant="caption" color="text.secondary">AI 思考中…</Typography>
          </Box>
        )}
      </Box>

      <Divider />

      {/* 輸入區 */}
      <Stack direction="row" spacing={1} sx={{ p: 1.5, alignItems: 'flex-end' }}>
        <TextField
          fullWidth multiline minRows={1} maxRows={6} size="small"
          placeholder={parentId ? '輸入訊息（Ctrl+Enter 送出）' : '請先選擇主物件'}
          value={input}
          onChange={e => setInput(e.target.value)}
          onKeyDown={handleKeyDown}
          disabled={!parentId || sending}
        />
        <Button variant="contained" size="small"
                onClick={handleSend}
                disabled={!input.trim() || !parentId || sending}
                sx={{ minWidth: 60 }}>
          <SendIcon fontSize="small" />
        </Button>
      </Stack>
    </Drawer>
  );
};

export default AiWorkspaceDrawer;
